/**
 * English copy. This object is the contract: `de.ts` is typed against it, so a
 * key added here must be translated before the build passes.
 */
export const en = {
  meta: {
    product: 'Hanseatic Select',
    company: 'Hanseatic LifeScience Consulting',
    tagline: 'Software vendor selection, built on evidence',
  },

  common: {
    or: 'or',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved',
    saveFailed: 'Could not save',
    cancel: 'Cancel',
    close: 'Close',
    remove: 'Remove',
    delete: 'Delete',
    add: 'Add',
    edit: 'Edit',
    done: 'Done',
    open: 'Open',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    create: 'Create',
    confirm: 'Confirm',
    copy: 'Copy',
    copied: 'Copied',
    copyFailed: 'Could not copy — select the text and copy it yourself',
    copyLink: 'Copy link',
    download: 'Download',
    popupBlocked: 'The browser blocked the new tab. Allow pop-ups for this site and try again.',
    search: 'Search',
    filter: 'Filter',
    all: 'All',
    none: 'None',
    yes: 'Yes',
    no: 'No',
    optional: 'optional',
    required: 'required',
    loading: 'Loading…',
    retry: 'Try again',
    signIn: 'Sign in',
    signOut: 'Sign out',
    email: 'Email',
    password: 'Password',
    name: 'Name',
    of: 'of',
    weight: 'Weight',
    vendor: 'Vendor',
    vendors: 'Vendors',
    criterion: 'Criterion',
    criteria: 'Criteria',
    requirement: 'Requirement',
    requirements: 'Requirements',
    stakeholder: 'Contributor',
    stakeholders: 'Contributors',
    evaluation: 'Evaluation',
    evaluations: 'Evaluations',
    status: 'Status',
    actions: 'Actions',
    language: 'Language',
    theme: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark',
    nothingYet: 'Nothing here yet',
    learnMore: 'Learn more',
    dismiss: 'Dismiss',
    print: 'Print',
    example: 'Example',
    statusDraft: 'Draft',
    statusScoring: 'Scoring',
    statusLocked: 'Locked',
    statusDecided: 'Decided',
  },

  nav: {
    workspace: 'Workspace',
    evaluations: 'Evaluations',
    team: 'Team & licences',
    settings: 'Settings',
    commandHint: 'Search or jump to…',
    commands: 'Commands',
    openCommand: 'Open the command palette',
    menu: 'Menu',
    account: 'Account',
    help: 'Help',
    openWorkspace: 'Open workspace menu',
    instanceAdmin: 'Instance admin',
  },

  notifications: {
    open: 'Notifications',
    title: 'Notifications',
    markAllRead: 'Mark all read',
  },

  stages: {
    request: 'Request',
    // One label under each stage id (request, urs, vendors, contributors,
    // results, evidence, decision): the workspace card and the command palette
    // look a label up by `t.stages[stage.id]`, so a stage without a key here
    // is a type error. The old `stakeholders` and `scoring` keys went when
    // those two stopped being read.
    evidence: 'Evidence',
    decision: 'Decision',
    requestHint: 'What you are buying, who is bidding, and what counts.',
    evidenceHint: 'Documents, vendor answers and the independent read of them.',
    decisionHint: 'The filed ranking, the sign-off and the decision pack.',
    urs: 'URS',
    contributors: 'Contributors',
    vendors: 'Vendors',
    results: 'Results',
    ursHint: 'The requirements vendors are measured against.',
    contributorsHint: 'The people who score, and how much each of them weighs.',
    vendorsHint: 'Who is bidding, and what each of them sent back.',
    resultsHint: 'How the vendors compare once contributors have scored them.',
    stage: 'Stage {n}',
    stepOf: 'Step {n} of {total}',
    fileDecision: 'File decision',
    fileDecisionBlocked: 'Lock the evaluation first. The Status card on this page is where a decision is filed.',
    weightsUnbalanced: 'Make every column of weights add up to 100% to continue',
    done: 'done',
    current: 'current',
    todo: 'to do',
  },

  evidence: {
    title: 'Evidence',
    lead: 'What the evidence says, kept separate from what the people said.',
    aiBadge: 'AI',
    documents: 'Documents',
    independent: 'Independent evaluation',
    matrix: 'Compliance matrix',
    clarify: 'Follow-ups and demo',
    commercial: 'Commercial',
    aiHalfTitle: 'AI analysis',
    resultsHalfTitle: 'Scoring results',
    findingsTitle: 'Findings',
    findingsEmpty: 'No findings on record yet.',
    include: 'Include',
    discard: 'Discard',
    included: 'Included',
    discarded: 'Discarded',
    scoringRecord: 'The scoring record',
  },

  auth: {
    workspaceLead: 'Sign in to {workspace}. This address serves your workspace only.',
    signInTitle: 'Sign in',
    signInLead: 'Use the email and password for your organisation workspace.',
    registerTitle: 'Create your workspace',
    registerLead: 'You become the first Owner. Your colleagues join by invitation.',
    orgName: 'Organisation',
    yourName: 'Your name',
    workEmail: 'Work email',
    createWorkspace: 'Create workspace',
    noAccount: 'No workspace yet?',
    haveAccount: 'Already have a workspace?',
    forgot: 'Forgotten your password?',
    forgotHelp:
      'Ask an Owner in your organisation to send you a reset link from Team & licences. If you are the only Owner, contact HLSC.',
    trialNote: 'Includes {days} days of every tool, AI included. No credit card.',
    passwordHint: 'At least 10 characters.',
    signInMicrosoft: 'Sign in with Microsoft',
    microsoftHint: 'Open Hanseatic Select from Microsoft Teams with your work account.',
    inviteTitle: 'Join {org}',
    inviteLead: 'Set a password to open your {plan} seat.',
    inviteAccept: 'Join the workspace',
    resetTitle: 'Set a new password',
    resetLead: 'Choose a new password for {email}.',
    resetCta: 'Save password and sign in',
    signedOut: 'You are signed out.',
    devPreview: 'Local preview',
    acceptTerms: 'I accept the {terms} and the {privacy}.',
    termsLinkLabel: 'Terms',
    privacyLinkLabel: 'Privacy Policy',
    registerPending: 'Check your inbox to continue, or sign in if you already have a workspace.',
    workspaceCreatedTitle: 'Workspace created',
    verifyRequiredTitle: 'Confirm your email address',
    verifyLinkFieldLabel: 'Verification link',
    verifyLinkShownOnce: 'Shown once — copy it now.',
    confirmAndContinue: 'Confirm and continue',
    continueToWorkspace: 'Continue to workspace',
    resendLink: 'Resend link',
    verifiedTitle: 'Email confirmed',
    resendSent: 'Check your inbox for a new link.',
  },

  sso: {
    // Deliberately not "Sign in with Microsoft" (`auth.signInMicrosoft`,
    // used by the Teams tab's own popup): on this screen that text would sit
    // right next to the password form's own "Sign in" button, and the two
    // are indistinguishable to a test — or a person — skimming for "sign in".
    continueButton: 'Continue with Microsoft',
    noAccount: 'No workspace account for that address yet. Ask an Owner to invite it, then try again.',
    cancelled: 'That Microsoft sign-in was cancelled.',
    noWorkspace: 'No workspace found for that email address. Sign in with your password instead.',
  },

  home: {
    title: 'Your evaluations',
    lead: 'Open a workshop file, continue scoring, or start a new vendor selection.',
    newEvaluation: 'New evaluation',
    fromDocuments: 'Start from a URS',
    fromDocumentsHint: 'Upload the URS and get a weighted criteria tree in about two minutes.',
    empty: 'No evaluations yet.',
    emptyLead: 'Start from your URS, or set the criteria up by hand.',
    activeEvaluations: 'Active evaluations',
    vendorsCompared: 'Vendors compared',
    stakeholdersScoring: 'Contributors scoring',
    decisionsFiled: 'Decisions filed',
    checklistTitle: 'Get to a defensible decision',
    checklistLead: 'Five steps. You can do them in any order, but this is the short path.',
    checklistCreate: 'Create the evaluation',
    checklistCriteria: 'Agree the criteria and weights',
    checklistContributors: 'Invite the contributors who score',
    checklistEvidence: 'Collect vendor evidence',
    checklistDecision: 'File the decision',
    contributorTitle: 'Your scorecard',
    contributorLead: 'Enter the PIN your owner sent you.',
    pinLabel: 'Seat PIN',
    openScorecard: 'Open scorecard',
    trialBanner: 'AI trial: {days} days left.',
    trialEnded: 'Your trial has ended. Your evaluations are kept; talk to HLSC to carry on with the AI tools.',
    trialCta: 'Keep the AI tools',
    demoBanner: 'Demo workspace — it disappears {when}. Nothing here is saved to your organisation.',
    demoCta: 'Create a free workspace',
    openEvaluationAria: 'Open {name}',
    deleteCta: 'Delete evaluation',
    deleteTitle: 'Delete this evaluation?',
    deleteBody: '{name} and everything scored in it will be removed. This cannot be undone.',
    deleteConfirm: 'Delete evaluation',
    deletedToast: 'Evaluation deleted.',
    archiveCta: 'Archive evaluation',
    archiveTitle: 'Archive this evaluation?',
    archiveBody: '{name} has a signed decision on file, so it is kept as a record rather than removed. It will leave your workspace list and can be restored later.',
    archiveConfirm: 'Archive evaluation',
    archivedToast: 'Evaluation archived.',
    archivedTitle: 'Archived evaluations ({n})',
    restoreCta: 'Restore',
    restoredToast: 'Evaluation restored to your workspace.',
  },

  wizard: {
    title: 'New evaluation',
    lead: 'Two short steps. Everything can be changed later.',
    step1: 'What are you buying?',
    step2: 'Which criteria?',
    projectName: 'Evaluation name',
    projectNamePlaceholder: 'MES selection — fill-finish',
    client: 'Client or site',
    clientPlaceholder: 'Hamburg plant',
    objective: 'What must the chosen vendor deliver?',
    objectivePlaceholder:
      'A GxP MES for the sterile fill-finish line: electronic batch records, Part 11 signatures, SAP integration and a GAMP 5 validation package.',
    addVendor: 'Add another vendor',
    templateLead: 'Start from an HLSC criteria template, or from a blank tree.',
    templateBlank: 'Standard tree (4 sections)',
    createCta: 'Create evaluation',
    needName: 'Give the evaluation a name.',
  },

  draft: {
    unreadableWord: 'Word files before .docx cannot be read here. Save it as .docx or PDF and try again.',
    unreadableBinary: 'This file is not a document the app can read. Use PDF, Word (.docx), TXT or Markdown.',
    title: 'Start from a URS',
    lead: 'Upload the URS and the RFP. The engine extracts the requirements, clusters them and proposes weighted criteria. You approve every line before anything is created.',
    dropzone: 'Drop the URS here, or choose files',
    dropzoneHint: 'PDF, Word (.docx), TXT or MD, up to 15 MB each. They are read in your browser.',
    kindUrs: 'URS',
    kindRfp: 'RFP request',
    kindOther: 'Other',
    files: 'Documents',
    noFiles: 'No documents yet.',
    hintName: 'Suggested name',
    hintClient: 'Client',
    hintTemplate: 'Template hint',
    run: 'Draft with AI',
    running: 'Drafting…',
    reviewTitle: 'Review the draft',
    reviewLead: 'Nothing is created until you approve it. Weights are percentages — adjust anything that looks wrong.',
    approve: 'Approve and create',
    discard: 'Discard',
    addGroup: 'Add section',
    addCriterion: 'Add criterion',
    expectation: 'What good looks like',
    sources: 'Sources',
    requirementsFound: '{n} requirements found',
    warnings: 'Warnings',
    createdBanner: 'Drafted from your documents. You approved {criteria} criteria in {groups} sections; the sources stay with the evaluation.',
    needDocuments: 'Add at least one document.',
    tooLarge: 'This file is over the 15 MB text limit. Split the URS or upload a smaller extract.',
    clipped: 'Only the first {n} characters of this document were used.',
  },

  request: {
    sourceTitle: 'Requirement document',
    sourceDrop: 'Drop a URS or RFP, or click to choose',
    draftWithAi: 'Draft criteria from this document',
    draftConfirm: 'Replace the criteria below with the ones drafted from this document?',
    drafted: '{n} criteria drafted',
    sectionsTotal: 'Sections',
    criteriaTotal: 'Criteria',
    title: 'Request',
    lead: 'What you are buying, who is bidding, and what counts.',
    details: 'Evaluation details',
    scale: 'Rating scale',
    scaleMin: 'Low (lowest rating)',
    scaleMax: 'High (highest rating)',
    scaleErrorWhole: 'Low and High must be whole numbers.',
    scaleErrorBounds: 'Low and High must each be between {floor} and {ceiling}.',
    scaleErrorOrder: 'Low must be less than High.',
    scaleMaxLocked: 'The rating scale cannot change once scores have been recorded, because the same number would mean something different on a different scale.',
    objective: 'Expectations / RFP context',
    criteriaTitle: 'Criteria and weights',
    criteriaLead: 'Weights are percentages of one whole. Each column of weights should add up to 100%.',
    groupWeight: 'Section weight',
    share: 'share',
    weightsOk: 'Weights are consistent.',
    addGroup: 'Add section',
    addCriterion: 'Add criterion',
    expectationLabel: 'What good looks like',
    expectationPlaceholder: 'The evidence a vendor must show to score well.',
    membersTitle: 'Who can edit this evaluation',
    membersLead: 'Members with an Owner licence can edit; contributors only score their own card.',
    memberEmail: 'Work email',
    membersHint: 'Owners run the evaluation and need an account here. Contributors do not — add them on Contributors, where each gets their own link and PIN.',
    addMember: 'Add member',
    activity: 'Activity',
    activityLead: 'Every change to this evaluation, newest first.',
  },

  assistant: {
    title: 'Assistant',
    open: 'Open the assistant',
    send: 'Send',
    proposed: 'Proposed change',
    confirm: 'Do it',
    placeholder: 'Ask about this evaluation, or say what to change',
    dock: 'Dock to the side',
    undock: 'Collapse to the corner',
  },
  seats: {
    title: 'Contributors',
    lead: 'The people who score. Each gets a private card nobody else can see.',
    addSeat: 'Add contributor',
    seatName: 'Role or name',
    voteWeight: 'Vote weight',
    voteWeightHint: 'How much this card counts in the filed ranking.',
    statusIssued: 'Invited',
    statusClaimed: 'Scoring',
    empty: 'No contributor cards yet. Add Quality, Production, IT, Engineering…',
    coverage: 'Scoring coverage',
    cells: '{n} cells on this card',
    colPerson: 'Person',
    colFunction: 'Function',
    colStatus: 'Status',
    colInvitation: 'Invitation',
    namePlaceholder: 'Full name',
    emailLabel: 'Email',
    emailPlaceholder: 'name@company.com',
    functionLabel: 'Function',
    functionPlaceholder: 'Quality, Production, IT…',
    copyLink: 'Copy link',
    sendByEmail: 'Send by email',
    remind: 'Send a reminder',
    reminded: 'Reminder sent',
    reissueLink: 'Issue a new link (the old one stops working)',
    reissuePin: 'Issue a new PIN (the old one stops working)',
    linkModalTitle: 'Send this link to {name}',
    linkModalLead: 'It is shown once. No account is needed — the link opens straight to their own card, once they enter the access code you give them separately.',
    pinModalTitle: 'New access code for {name}',
    pinModalLead: 'It is shown once.',
    pinFieldLabel: 'Access code',
    copyPin: 'Copy code',
    pinCopied: 'Access code copied.',
    pinSeparateWarning: 'Pass this code to {name} a different way than the link — a call, a message, in person. Never the same email or message: anyone who has both can open this card.',
    statusScored: 'Scored',
    statusInvited: 'Invited',
    statusNotInvited: 'Not invited',
    privacyNote: 'Scores stay private to each contributor until you close scoring. Not to you, not to an operator, not to the assistant.',
    inviteCopied: 'Invitation link copied.',
    countLabel: '{n} contributors',
  },

  // Three new stages in the seven-step process. Each is a placeholder for
  // this phase — a heading and one sentence — built out properly once the
  // step that owns it lands.
  urs: {
    title: 'URS',
    // State 1 — nothing uploaded yet.
    dropzone: 'Upload your URS, or start from a template.',
    dropzoneHint: 'PDF or Word (.docx), up to 15 MB. Read in your browser — nothing is uploaded until you choose a file.',
    startFromTemplate: 'Start from a template',
    chooseTemplate: 'Choose a template',
    // State 2 — extracting.
    extracting: 'Reading the document…',
    // State 3 — the review table.
    reviewTitle: 'Requirements',
    uploadAnother: 'Upload another document',
    extractFromStored: 'Extract from URS',
    storedUnreadable: 'That document has no readable text.',
    colGroup: 'Group',
    colRequirement: 'Requirement',
    colWeight: 'Weight',
    colPriority: 'Must / should',
    colSource: 'Source',
    priorityMust: 'Must',
    priorityShould: 'Should',
    addRow: 'Add requirement',
    newGroup: 'New group',
    addedByYou: 'Added by you',
    removedRow: 'Requirement removed.',
    balance: 'Balance sections',
    undo: 'Undo',
    save: 'Save',
    noRows: 'No requirements yet.',
  },
  vendors: {
    title: 'Vendors',
    lead: 'Who is bidding, what you asked them to supply, and what came back.',
    addVendor: 'Add vendor',
    vendorNameLabel: 'Vendor name',
    colName: 'Vendor',
    colContact: 'Contact',
    colStatus: 'Portal status',
    colSubmitted: 'Submitted',
    statusNotInvited: 'Not invited',
    manageLink: 'Manage link',
    empty: 'No vendors yet. Add the companies bidding on this evaluation.',
    selectVendor: 'Select a vendor to see their documents, questions and commercial figures.',

    requestsTitle: 'Requests to vendors',
    documentsHeading: 'Documents requested',
    questionsHeading: 'Questions asked',
    documentsEmpty: 'No documents requested yet. Add what every vendor must send.',
    questionsEmpty: 'No questions yet. Add what you need every vendor to answer.',
    requestDocument: 'Request document',
    askQuestion: 'Ask question',
    questionLabel: 'Question',
    questionPlaceholder: 'How many validated sites run this release?',
    answerFormatLabel: 'Answer format',
    requestNameLabel: 'Document name',
    requestNamePlaceholder: 'Validation summary report',
    requestDescriptionLabel: 'Description',
    requestMandatoryLabel: 'Mandatory',
    kindFile: 'File upload',
    kindText: 'Short answer',
    kindLongtext: 'Paragraph answer',
    kindNumber: 'Number',
    kindDate: 'Date',
    kindChoice: 'Pick one',
    kindBoolean: 'Yes / no',
    requestFormatsLabel: 'Accepted formats',
    requestOptionsLabel: 'Options (one per line)',
    requestAppliesToLabel: 'Applies to',
    appliesAll: 'All vendors',
    appliesSpecific: 'Specific vendors',
    moveUp: 'Move up',
    moveDown: 'Move down',
    mandatoryBadge: 'Mandatory',
    optionalBadge: 'Optional',
    saveAsTemplate: 'Save list as template',
    templateNameLabel: 'Template name',
    templateNamePlaceholder: 'Standard document pack',
    applyTemplateLabel: 'Add requests from a template',
    applyNoVendors: 'No vendors yet. These requests will go to every vendor you add.',
    applyTemplateCta: 'Add',
    noTemplates: 'No templates yet.',
    templateSaved: 'Template saved',
    templateApplied: 'Template applied',
    sendTitle: 'Generate and send the link',

    tabDocuments: 'Documents',
    tabQuestions: 'Follow-ups',
    tabCommercial: 'Commercial',
    previewTitle: 'Preview — what the vendor will see',
    edit: 'Edit',

    checklistNotUploaded: 'Not yet uploaded',
    aiAllowedLabel: 'Use in AI analysis',
    view: 'View',
    otherDocuments: 'Other documents',
    uploadedAt: 'Uploaded {date}',
    portalLinkTitle: 'Portal link',
    portalManage: 'Manage in Documents',

    // The vendor form the Owner generates here and sends out: the approved
    // requirements plus the attachment section configured above.
    countLabel: '{n} vendors',
    generateForm: 'Generate vendor form',
    formTitle: 'Vendor form — {vendor}',
    formLead: 'This is the form {vendor} fills in. Generate a link to send it.',
    formRequirementsTitle: 'Requirements to answer',
    formRequirementsSource: '{count} approved requirements · source: {source}',
    formRequirementsEmpty: 'No approved requirements yet — add them in step 2, URS.',
    formAttachmentsTitle: 'Documents and answers requested',
    formAttachmentsEmpty: 'No document requests yet — add them above.',
    formVendorFills: 'Vendor fills in',
    formContactTitle: 'Send to',
    generateLink: 'Generate link',
    linkReady: 'The link is the credential — it works without an account and only for this vendor.',
    colRequirementId: 'ID',
    colRequirement: 'Requirement',
    colSection: 'Section',
  },
  results: {
    colScored: 'Scored',
    scoredOf: '{scored} of {total}',
    title: 'Scoring Results',
    lead: 'What the scoring produced. Individual answers appear only once scoring is closed.',
    openBanner: 'Scoring is open. Individual answers stay hidden until it is locked.',
    lockAction: 'Lock scoring',
    lockConfirmBody: 'This reveals every contributor’s own answers and cannot be undone. Lock scoring for this evaluation?',
    lockedToast: 'Scoring is locked.',
    lockedBadge: 'Scoring locked',
    rankingTitle: 'Vendor ranking',
    marginLabel: '{leader} leads {runnerUp} by {gap}.',
    scoreLegend: 'Score',
    spreadTitle: 'Disagreement by criterion',
    perContributorTitle: 'Per contributor',
    noContributors: 'No contributor has scored yet.',
  },

  documents: {
    title: 'Documents',
    lead: 'The URS, the RFP, and what each vendor sent back. Everything stays in your workspace.',
    addTitle: 'Add a document',
    kindUrs: 'URS',
    kindRfp: 'RFP / RFQ',
    kindTechnical: 'Technical offer',
    kindCommercial: 'Commercial offer',
    kindQa: 'Q&A',
    kindPresentation: 'Presentation',
    kindDemoNotes: 'Demo notes',
    kindRequirements: 'Requirement list',
    forVendor: 'For vendor',
    dropOrPaste: 'Drop a file, or paste the text',
    pastePlaceholder: 'Paste the document text',
    stored: 'Stored documents',
    noDocuments: 'No documents yet. Start with the URS or the RFP.',
    requirementsGroup: 'Requirement list',
    fromUrsStep: 'Added at the URS step.',
    fromVendorLink: 'Arrives through this vendor’s link.',
    otherDocuments: 'Sent another way',
    characters: '{n} characters',
    templateCta: 'Download response template',
    portalTitle: 'Ask vendors directly',
    portalLead: 'Send each vendor a private link. They answer the requirement list and upload their offer — no account needed.',
    portalInvite: 'Create link',
    portalContact: 'Contact name',
    portalEmail: 'Contact email',
    portalAnswered: '{answered} of {total} answered',
    portalStatusSent: 'Link sent',
    portalStatusOpened: 'Opened',
    portalStatusInProgress: 'In progress',
    portalStatusSubmitted: 'Submitted',
    portalStatusLate: 'Late',
    portalStatusRevoked: 'Revoked',
    portalStatusExpired: 'Expired',
    portalRevoke: 'Revoke link',
    portalNewLink: 'New link',
    portalLinkTitle: 'Send this link to {vendor}',
    portalLinkLead: 'It is shown once. Only a hash is stored, so a replacement link can always be issued.',
    portalCopied: 'Vendor link copied',
    portalEmpty: 'No vendor links yet.',
    portalReopen: 'Reopen response',
    portalSubmissions: '{n} submissions',
    portalReopenedAt: 'Reopened {date}',
    portalDueLabel: 'Due date',
    portalDueAt: 'Due {date}',
    portalRemind: 'Send a reminder',
    portalReminded: 'Reminder sent',
    portalRemindedAt: 'Reminder sent {date}',
    portalReissue: 'Issue a new link (the old one stops working)',
  },

  ai: {
    title: 'Independent evaluation',
    lead: 'A reproducible read of the offers: term coverage first, then a language review, then a calibrated number. The model never writes the score.',
    run: 'Run independent evaluation',
    running: 'Running…',
    lastRun: 'Last run {duration} · {engine}',
    showSteps: 'Show steps',
    hideSteps: 'Hide steps',
    tabRequirements: 'Requirement coverage',
    tabGaps: 'Gap analysis',
    tabScores: 'Independent scores',
    tabIntegrity: 'Integrity',
    tabFollowUps: 'To chase',
    followUpsClear: 'Every requirement is answered by at least one bid, in detail. Nothing outstanding to chase.',
    followUpUnanswered: 'Answered by nobody',
    followUpThin: 'Answered only loosely',
    followUpSole: 'Only one bid',
    followUpDeferred: 'Deferred',
    followUpMustCount: '{n} of them must-have',
    followUpUnansweredTitle: 'No bid answered these',
    followUpUnansweredLead: 'Nothing in any accepted response addresses these requirements. Put them in the clarification round, or drop them from the scorecard if they are no longer required.',
    followUpThinTitle: 'Answered, but not in a way you could hold them to',
    followUpThinLead: 'Someone addressed these, but no bid gave a specific enough answer to verify. Ask how, not whether.',
    followUpDeferredTitle: 'Deferred or excluded',
    followUpDeferredLead: 'The vendor’s own words put this on a roadmap, out of scope, or at extra cost. Get a date and a price before it becomes an assumption.',
    followUpSoleTitle: 'Only one bid covers these',
    followUpSoleLead: 'A single supplier answers these requirements. That is either a real differentiator or a gap the others were never asked to close — worth deciding which before it drives the award.',
    tabRecommendation: 'Recommendation',
    needDocuments: 'Upload at least one vendor offer first.',
    howTitle: 'How the number is formed',
    howLead:
      'Lexical evidence counts criterion terms in the offer. The language review labels each cell support, refute, mixed or absent, including negation. The calibrated score is 35% lexical and 65% language. Quotes that are not verbatim in the offer are discarded.',
    coverage: 'coverage',
    quotes: 'Quotes',
    noQuotes: 'No quotes for this cell.',
    integrityTitle: 'Contributor credibility',
    integrityLead:
      'Facts only. A high-risk card counts for less in the advisory recommendation. Nobody is accused: scores are compared with the panel and with the offers.',
    overallRisk: 'Overall integrity risk',
    integrityBandHigh: 'High-risk cards',
    integrityBandMedium: 'Medium-risk cards',
    integrityBandLow: 'Low-risk cards',
    findings: 'Findings',
    noFindings: 'No integrity findings.',
    recommendationTitle: 'Fact-weighted recommendation',
    advisory: 'Advisory',
    advisoryNote: 'Advisory only. The filed ranking is the contributor mean.',
    recommended: 'Recommended',
    tooClose: 'Too close to call',
    tooCloseHint: 'on the documents alone',

    evidenceWeight: 'Evidence weight',
    expectationMatch: 'Expectation match',
    expectationsMet: 'Expectations met',
    expectationGaps: 'Expectation gaps',
    caveats: 'Caveats',
    combinedRanking: 'Combined ranking',
    independent: 'Independent',
    stakeholdersColumn: 'Contributors',
    finalColumn: 'Final',
    gapsLead: 'Where the independent read and the panel disagree. Flag a cell to bring it into the workshop and the decision pack.',
    gapFlag: 'Flag',
    gapFlagged: 'Flagged',
    gapNote: 'Workshop note',
    gapEmpty: 'No differences for this filter.',
    aiEvidence: 'AI evidence',
    notRun: 'Not run yet.',
    notRunLead: 'Upload the vendor offers, then run the independent evaluation.',
    irrelevant: 'This document does not look like an offer for this evaluation.',
    aiSplitRaised: 'Raised above the default 45/55 split because {reason}.',
    aiSplitReasonCoverage: 'coverage is low',
    aiSplitReasonBias: 'panel bias was detected',
    aiSplitReasonBoth: 'coverage is low and panel bias was detected',

    // AI dual-mode (flow upgrade, 5 Oct 2026): the AI contributor (Mode 1)
    // and AI analysis of contributor scoring (Mode 2), and the Decision
    // page's two record-keeping switches. Never the stack, a provider or a
    // model name — "the AI contributor" and "the AI's analysis" throughout.
    contributionTitle: 'AI contribution',
    contributorSeatLabel: 'AI contributor',
    contributorCountedToggle: "Count the AI contributor's scores in the ranking",
    analysisIncludedToggle: "Attach the AI's analysis as an annex",
    countedOff: 'Off (the default): the AI contributor scores blind and independent of the people, but its scores are left out of the ranking.',
    countedOn: 'On: its scores join the weighted mean alongside the human contributors, like one more contributor.',
    countedCoverage: 'Either way the AI never counts towards coverage. Only people do, so switching this on cannot make a thinly scored evaluation look complete.',
    recomputing: 'Updating the ranking…',
    countedNowOn: 'The AI contribution now counts in the ranking.',
    countedNowOff: 'The ranking no longer counts the AI contribution.',
    // The AI contributor's run, and the plain facts the switch above depends on.
    // These state what is true now; the switch cannot change a ranking until the
    // AI contributor has scored, and the owner stood here wondering why it did not.
    contributorRun: 'Run the AI contributor',
    contributorRerun: 'Run it again',
    contributorRunning: 'Scoring…',
    contributorRan: 'The AI contributor scored {n} cells.',
    contributorNotScored: 'The AI contributor has not scored this evaluation yet, so counting it cannot change the ranking. Run it first.',
    contributorNotScoredLocked: 'Scoring was locked before the AI contributor scored, and it can no longer be run, so counting it would change nothing.',
    contributorScored: 'Scored {n} of {total} cells from the vendors’ documents by the AI, on {date}.',
    contributorUnrecorded: 'This card holds {n} scores, but how they were made was not recorded, so they cannot be shown to come from an AI read. Do not count them unless you know where they came from.',
    // The annex switch: disabled until there is an analysis to attach.
    analysisNone: 'There is no AI analysis to attach yet. Run the independent read first.',
    analysisBuiltIn: 'The stored independent read was made by built-in rules, not by the AI, so there is no AI analysis to attach. Run it again.',
    analysisNeedsLock: 'The annex can be attached once scoring is locked.',
    // A report stored before the AI analysis stopped falling back to rules.
    builtInReport: 'This independent read was made by built-in rules, not by the AI. It is kept as it was recorded; run it again to replace it with an AI read.',
    builtInBadge: 'Built-in rules',
  },

  // Why an AI run did not happen. One sentence per cause, each saying what to do.
  // The server refuses with a code (src/api/ai/availability.js) and writes
  // nothing — there is no built-in-rules substitute for a model read.
  aiErrors: {
    notConfigured: 'The AI is not set up on this installation, so nothing was analysed. Ask whoever runs this installation to connect it.',
    allowanceExhausted: 'This workspace has used its AI allowance, so nothing was analysed. Request more credits in Settings, or wait for the allowance to reset.',
    noDocuments: 'There are no vendor documents to read yet. Upload the vendors’ offers first, then run it again.',
    timeout: 'The AI took too long to answer, so nothing was analysed and nothing has changed. Try again; fewer or shorter documents help.',
    failed: 'The AI could not complete the analysis, so nothing was analysed and nothing has changed. Try again in a moment.',
    locked: 'Scoring is locked, so the AI can no longer score this evaluation.',
    notInPlan: 'The AI is not part of this workspace’s plan. Ask your workspace administrator about adding it.',
    lapsed: 'This workspace’s AI trial or subscription has ended. Ask your workspace administrator to renew it.',
    switchedOff: 'The AI tools are switched off for this workspace. Ask your workspace administrator.',
    alreadyRunning: 'An analysis is already running for this evaluation. Wait for it to finish.',
  },

  matrix: {
    title: 'Compliance matrix',
    lead: 'Requirement by vendor. Answers come from the vendor portal and from the uploaded offers.',
    refresh: 'Refresh matrix',
    full: 'Full',
    partial: 'Partial',
    no: 'No',
    future: 'Future',
    unanswered: 'Unanswered',
    flags: 'Flags',
    noFlags: 'No flags.',
    flagYesWithoutExplanation: 'Claimed without explanation',
    flagUnanswered: 'Unanswered',
    flagFutureAsFull: 'Roadmap sold as available',
    flagAllVendorsNo: 'No vendor covers this must-requirement',
    flagIrrelevantDocument: 'Document does not match this evaluation',
    empty: 'Nothing to compare yet.',
    emptyLead: 'Send the vendors a link, or upload their offers in Documents.',
    onlyMust: 'Must-requirements only',
    filterVendor: 'Vendor',
    matchSemantic: 'Inferred from prose',
    boilerplateTitle: 'Repeated answers',
    boilerplateVendor: '{vendor} — {pct}% of answers repeated',
    boilerplateGroup: '{n} requirements: {ids}',
    boilerplateEmpty: 'No repeated answers.',
    flagMore: '…and {n} more',
    flagShowAll: 'Show all {n}',
    flagShowFewer: 'Show fewer',
    flagGoTo: 'Show {id} in the matrix',
    flagWholeVendor: '{vendor} — all {n} requirements',
    aboutWhat: 'One row for each approved requirement, one column for each vendor — to see at a glance who answered what, and where nobody did.',
    aboutHow: 'Each cell fills in by itself from the answers the vendor submitted through its portal link and from the offers you uploaded under Documents, portal answers first. An answer is matched to a requirement by the requirement ID the vendor quoted; failing that, by at least two terms the answer shares with the requirement. Fixed rules, no AI model: the same submissions always give the same matrix.',
    aboutWeaker: 'A cell matched by shared terms rather than by ID carries the badge “{badge}”. That is a weaker signal — read the quote before relying on it.',
  },

  clarify: {
    title: 'Follow-ups and demo',
    lead: 'Follow-ups drafted from the matrix flags, and the script for what still has to be shown live.',
    draftQuestions: 'Draft follow-ups',
    buildScript: 'Build demo script',
    copyQuestions: 'Copy follow-ups',
    questions: 'Follow-ups to chase',
    demoScript: 'Demo script',
    demonstrated: 'Demonstrated',
    noQuestions: 'No follow-ups yet. Refresh the matrix first — follow-ups come from its flags.',
    noScript: 'No demo script yet.',
  },

  tco: {
    title: 'Commercial',
    lead: 'Figures read out of the commercial offers. Never scored by the AI, and only the owner sees this.',
    extract: 'Extract figures',
    category: 'Category',
    amount: 'Amount',
    missing: 'Missing from this offer',
    noFigures: 'No figures extracted yet.',
    total: 'Sum of the listed figures',
    totalHint: 'Only the lines above — not a full TCO model.',
    empty: 'Upload the commercial offers in Documents, then extract the figures.',
  },

  scoring: {
    title: 'Scoring',
    lead: 'Each contributor rates every vendor on every criterion.',
    myCard: 'Scorecard: {name}',
    myCardLead: 'Rate and add the evidence behind the rating. Changes save as you type.',
    progress: 'Card complete',
    locked: 'This evaluation is locked. Scores are read-only.',
    comment: 'Evidence note',
    commentPlaceholder: 'What did you see that supports this rating?',
    coverageTitle: 'Coverage by contributor',
    coverageLead: 'Peer scores stay hidden so ratings stay independent.',
    seatState: 'State',
    notStarted: 'Not started',
    inProgress: 'Scoring',
    inProgressCount: '{scored} of {total} scored',
    complete: 'Complete',
    noSeats: 'No contributor cards yet — issue PINs first.',
    claimOwn: 'Claim a card if you also score.',
    ratingLabel: 'Rating {min} to {max}',
    criterionPosition: 'Criterion {n} of {total}',
    ratedCount: '{filled} of {total} rated',
    nextCriterion: 'Next criterion',
    addNote: 'Add note',
    keyRate: 'rate',
    keyNextVendor: 'next vendor',
    keyNextCriterion: 'next criterion',
    noCriteria: 'No criteria to score yet.',
    finish: 'Finish',
    finishReviewTitle: '{n} of {total} ratings still missing',
    finishMore: 'and {n} more',
    finishRateMissing: 'Rate the missing ones',
    finishAnyway: 'Finish without them',
    finishDoneTitle: 'Thank you. Your scoring is submitted.',
    finishDoneAll: 'All {total} ratings are recorded.',
    finishDoneSome: '{filled} of {total} ratings are recorded; {missing} left unrated.',
    finishReview: 'Review my ratings',
    finishHome: 'Back to my evaluations',
    finishResults: 'Back to the results',
  },

  decision: {
    title: 'Decision',
    lead: 'The filed ranking is the weighted contributor mean. The AI read is advisory and always shows its evidence.',
    officialRanking: 'Filed ranking',
    officialHint: 'Weighted contributor mean — this is the number of record.',
    topVendor: 'Leading vendor',
    margin: 'Margin to second',
    coverage: 'Coverage',
    cards: 'Contributor cards',
    sectionComparison: 'Section comparison',
    compareCta: 'Compare with and without the AI',
    compareTitle: 'The ranking with and without the AI',
    compareVendor: 'Vendor',
    compareWithout: 'Without the AI',
    compareWith: 'With the AI',
    compareDifference: 'Difference',
    compareRank: 'Rank {n}',
    compareCurrent: 'Counted now',
    compareMovedUp: 'Up {n}',
    compareMovedDown: 'Down {n}',
    compareSameRank: 'Same rank',
    compareWinnerChanged: 'Including the AI changes the winner: {without} leads without it, {with} leads with it.',
    compareOrderChanged: 'Including the AI changes the order of the vendors. {winner} leads either way.',
    compareScoresChanged: 'Including the AI changes the scores but not the order. {winner} leads either way.',
    compareNothing: 'Including the AI changes nothing: the same order and the same scores.',
    compareNoAi: 'The AI contributor has not scored this evaluation yet, so counting it changes nothing and there is nothing to compare.',
    compareUnrecorded: 'How the AI contributor’s scores were made was not recorded, so they cannot be shown to come from an AI read.',
    compareNoScores: 'Nobody has scored yet, so there is nothing to compare.',
    compareSections: 'Change in score by section',
    heatmap: 'Section heatmap',
    statusTitle: 'Status',
    statusLead: 'Draft → scoring → locked → decided. Locking makes scores read-only; decided is final.',
    toScoring: 'Move to scoring',
    lock: 'Lock evaluation',
    reopen: 'Return to scoring',
    markDecided: 'Mark as decided',
    lockConfirm: 'Lock this evaluation? Scores become read-only. Locking needs at least one contributor who has joined and at least one score. If coverage is below the minimum, you will be asked to confirm before it goes ahead.',
    decideConfirm: 'File this decision? The evaluation moves to decided, and the filing is recorded in the audit trail with who did it and when. The same checks apply as for locking, including the confirmation if coverage is below the minimum. A signature is a separate step (“{sign}”). A decided evaluation can be reopened, but only with a written reason, and doing so voids every signature.',
    signRecords: 'Signing records your name, the time, what your signature means, and a fingerprint of the evaluation as it stands now. If the evaluation changes afterwards, or is reopened, the signature no longer covers it and is marked so.',
    packTitle: 'Decision pack',
    packLead: 'One document: ranking, weights, evidence, gaps, signatures and the audit trail.',
    packOpen: 'Open decision pack',
    signTitle: 'Sign-off',
    signLead: 'Record who stands behind this decision and in what capacity.',
    signCta: 'Sign this decision',
    signMeaning: 'I am signing as',
    signAuthored: 'Author — I prepared this evaluation',
    signReviewed: 'Reviewer — I checked it',
    signApproved: 'Approver — I approve this outcome',
    signReason: 'Meaning of your signature',
    signReasonPlaceholder: 'Approved on behalf of Quality.',
    signPassword: 'Confirm with your password',
    signPasswordHint: 'A signature is an act: your password confirms it is you.',
    signed: 'Signed',
    signedBy: '{name} · {meaning}',
    noSignatures: 'No signatures yet.',
    signNote: 'Decision-support evidence. This is not a validated Part 11 signature system.',
    sealValid: 'Covers the current content',
    sealInvalid: 'The evaluation changed since this was signed — re-approval is needed.',
    sealSuperseded: 'Superseded by a reopen — this signature no longer applies.',
    sealHash: 'Content {hash}',
    completeness: '{pct}% complete',
    missingSections: 'Missing: {sections}',
    stakeholderMargin: 'Contributor ranking gap',
    blendedMargin: 'Blended score gap',
    reopenTitle: 'Reason for reopening',
    reopenLead: 'Reopening returns this evaluation to {status} and supersedes every existing signature.',
    reopenReason: 'Reason',
    reopenReasonPlaceholder: 'Why is this being reopened?',
    reopenCta: 'Reopen',
    lowCoverageCta: 'File anyway',
    expertTitle: 'Want a second opinion?',
    expertLead:
      'HLSC consultants review the evaluation, the evidence and the decision pack, and join the decision meeting if you want them there.',
    expertCta: 'Request an HLSC expert review',
    expertScope: 'What should we look at?',
    expertMessage: 'Anything we should know?',
    expertSent: 'Request sent. HLSC will get in touch by email.',
    noScores: 'No contributor scores yet — issue PINs in Contributors.',
  },

  team: {
    title: 'Team & licences',
    lead: 'Who is in this workspace, what they may do, and how many seats are left.',
    evaluators: 'Owners',
    contributors: 'Contributors',
    evaluatorHint: 'Create evaluations, issue PINs, file decisions.',
    contributorHint: 'Score their own card only.',
    inviteTitle: 'Invite a colleague',
    inviteLead: 'They get a link, set their own password, and land in this workspace.',
    inviteCta: 'Create invitation',
    invitePending: 'Pending invitations',
    inviteLink: 'Invitation link',
    inviteLinkHint: 'Send this link yourself — no mail server is configured.',
    inviteEmailed: 'We emailed the invitation to {email}.',
    inviteRevoke: 'Withdraw',
    members: 'People',
    plan: 'Licence',
    makeEvaluator: 'Make Owner',
    makeContributor: 'Make Contributor',
    removeLicence: 'Remove licence',
    resetPassword: 'Send reset link',
    resetLinkReady: 'Password reset link for {email}',
    noMembers: 'Only you so far.',
    seatsLeft: '{n} left',
    demoBlocked: 'Invitations are disabled in the demo workspace.',
  },

  instanceAdmin: {
    title: 'Instance admin',
    evaluatorSeats: 'Owner seats',
    contributorSeats: 'Contributor seats',
    pendingInvites: 'Pending invitations',
    plan: 'Plan',
    mail: 'Email',
    mailConfigured: 'Configured ({transport})',
    mailNotConfigured: 'Not configured — links are shown on screen instead',
    people: 'People',
    invitations: 'Invitations',
    role: 'Role',
    roleChanged: 'Role changed',
    resetPassword: 'Send reset link',
    resetLinkReady: 'Password reset link for {email}',
    signOut: 'Sign out everywhere',
    remove: 'Remove',
    withdraw: 'Withdraw',
    noPeople: 'Only you so far.',
    noInvitations: 'None pending.',
    creditRequests: 'Capacity requests',
    grantAmount: 'Amount',
    grant: 'Grant',
    dismiss: 'Dismiss',
    creditGranted: 'Credit granted',
    creditRequestDismissed: 'Request dismissed',
    noCreditRequests: 'No open requests.',
  },

  // Consoles & mail wave (6 Oct 2026): the instance admin's own policy
  // choices and small libraries — new sections on the Instance admin
  // screen, kept in their own namespace rather than widening `instanceAdmin`
  // above, which already existed before this change.
  consoles: {
    lockPolicyTitle: 'Who may lock scoring',
    lockPolicyLead: 'Locking scoring is irreversible and reveals every contributor’s own scores.',
    lockPolicyOwnerOnly: 'Only the evaluation’s owner',
    lockPolicyAnyEvaluator: 'Any Owner on the evaluation',
    lockPolicySaved: 'Saved',
    defaultAiTitle: 'Default AI setting for new evaluations',
    defaultAiLead: 'The starting position for a new evaluation’s AI toggle. Anyone creating one can still change it.',
    defaultAiOn: 'On',
    defaultAiOff: 'Off',
    defaultAiSaved: 'Saved',
    documentTemplatesTitle: 'Document-request templates',
    documentTemplatesLead: 'Your own reusable request lists, plus HLSC’s shared library below.',
    documentTemplatesShared: 'Shared library',
    documentTemplatesOwn: 'Your workspace',
    documentTemplatesSaveCopy: 'Save a copy for your workspace',
    documentTemplatesCopySaved: 'Copy saved to your workspace',
    documentTemplatesName: 'Template name',
    documentTemplatesCreate: 'Save as a new template',
    documentTemplatesRemove: 'Remove',
    documentTemplatesEmpty: 'Nothing saved yet.',
    criteriaTemplatesTitle: 'Criteria templates',
    criteriaTemplatesLead: 'Your workspace’s own saved groups and criteria — visible to nobody else.',
    criteriaTemplatesName: 'Template name',
    criteriaTemplatesCreate: 'Save current as a template',
    criteriaTemplatesRemove: 'Remove',
    criteriaTemplatesEmpty: 'Nothing saved yet.',
    lockedRegisterTitle: 'Locked evaluations',
    lockedRegisterLead: 'Evaluations in your workspace whose scoring is locked — metadata only.',
    lockedRegisterEvaluation: 'Evaluation',
    lockedRegisterLockedBy: 'Locked by',
    lockedRegisterLockedAt: 'Locked at',
    lockedRegisterEmpty: 'Nothing locked yet.',
  },

  // The "Send" half of every "Copy" (web/src/ui/send.tsx). One namespace,
  // used wherever that shared control appears.
  share: {
    send: 'Send by email',
    sendFallback: 'Nothing opened? Then no email program is set up on this device — copy the link instead.',
    hello: 'Hello {name},',
    helloAnon: 'Hello,',
    portalSubject: '{project}: documents and answers requested from {vendor}',
    portalIntro: 'We are comparing vendors for “{project}” and would like {vendor} to take part. This is your personal link:',
    portalAsk: 'Please open it, answer the requirement questions and upload the documents we ask for. The link works only for {vendor} — please do not forward it.',
    portalDue: 'Please send your answers by {date}.',
    contributorSubject: '{project}: your scoring card',
    contributorIntro: 'You have been asked to score the vendors in “{project}”. This is your personal link to your private scorecard:',
    contributorAsk: 'Open it and rate each vendor against each criterion. You will also need an access code, which you will receive separately.',
    rankingSubject: '{project}: vendor ranking',
    rankingIntro: 'Here is the filed ranking for “{project}”, read-only:',
    rankingAsk: 'The page shows the ranking only.',
    inviteSubject: 'Your invitation to {workspace} on Hanseatic Select',
    inviteIntro: 'You have been invited to join {workspace}. Open your personal link to set up your access:',
    inviteAsk: 'The link is for you only — please do not forward it.',
    inviteWorkspaceFallback: 'our workspace',
    resetSubject: 'Reset your password for {workspace}',
    resetIntro: 'A password reset was requested for your account in {workspace}. Open your personal link to choose a new password:',
    resetAsk: 'If you did not expect this, tell your workspace owner. The link is for you only.',
    contactSubject: 'Enquiry about Hanseatic Select',
    contactIntro: 'I would like to talk to you about Hanseatic Select.',
    contactAsk: 'Name:\nOrganisation:\nMy role:\nWhat we are selecting:',
    contactFallback: 'Nothing opened? Write to {email} from your own mail program.',
  },

  settings: {
    title: 'Workspace settings',
    organisation: 'Organisation',
    plan: 'Plan',
    planBasic: 'Basic',
    planAi: 'AI',
    planTrial: 'AI trial',
    planBasicLead: 'Scoring, ranking and decision pack, without the AI tools.',
    planAiLead: 'Draft from a URS, independent evaluation, matrix, follow-ups and commercial extraction.',
    trialEnds: 'Trial ends {date}',
    aiExpires: 'AI subscription runs to {date}',
    upgradeCta: 'Talk to HLSC about keeping AI',
    dataTitle: 'Data and AI processing',
    dataLead:
      'Evaluation text is processed to produce the independent read. On the hosted service that call goes to an external model provider; a private instance can point at your own model.',
    licenceKey: 'Organisation licence key',
    activate: 'Activate',
    navigation: 'Navigation',
    navSidebar: 'Sidebar',
    navBottomBar: 'Bottom bar',
    aiUsageTitle: 'AI usage',
    aiUsageFeature: 'Feature',
    aiUsageUsed: 'Used',
    aiUsageAllowance: 'Allowance',
    aiUsageCredits: 'Credits',
    aiUsageResets: 'Resets {date}',
    aiUsageRecent: 'Recent activity',
    aiKindEvaluation: 'Independent evaluation',
    aiKindDrafting: 'Drafting from a URS',
    aiKindAssistant: 'Assistant',
    aiKindTender: 'Compliance matrix and follow-ups',
    aiKindAny: 'Any feature',
    aiRequestMore: 'Request more',
    aiRequestTitle: 'Request more AI credits',
    aiRequestKind: 'Feature',
    aiRequestNote: 'Note',
    aiRequestSend: 'Send request',
    aiRequestSent: 'Request sent',
    aiBuyCredits: 'Buy credits',
    aiBuyCreditsAskAdmin: "Ask your instance admin to buy more credits",
    aiBuyCreditsTitle: 'Buy AI credits',
    aiBuyPacks: 'Packs of 1,000 credits',
    aiBuyPackPrice: '€{eur} per pack of {size} credits',
    aiBuySubmit: 'Continue to payment',
    aiUsageOf: '{used} of {total}',
    aiUsageByFeature: 'Usage by feature',
    aiUsageShareLabel: '{percent}% of usage this period',
    aiUsageCreditBalance: 'Credit balance',
    aiUsageSharedCredit: 'Shared credit, any feature',
    aiUsageCreditRemaining: '{remaining} of {granted} remaining',
    aiUsageNoCredit: 'No credit granted',
    aiUsageLastUpdated: 'Updated {time}',
    aiUsageRefresh: 'Refresh',
    aiUsageProjectionWarning: 'At this pace, {feature} will run out before the reset on {date}.',
    aiUsageOverallProjectionWarning: 'At this pace, your AI usage will run out before the reset on {date}.',
    aiUsagePeriodLabel: 'This period',
    aiUsageExtraCapacity: 'Extra capacity',
    aiUsageUnitsOf: '{used} of {total} AI units',
  },

  portal: {
    title: 'Requirement response',
    lead: '{client} invited {vendor} to answer the requirement list for “{project}”.',
    intro:
      'Answer each requirement, add the evidence, and upload your offer. You can come back to this link until you submit.',
    compliance: 'Compliance',
    explanation: 'Explanation',
    reference: 'Offer reference',
    referencePlaceholder: 'Section 3.1',
    explanationPlaceholder: 'How do you meet this requirement?',
    progress: '{answered} of {total} answered',
    saveDraft: 'Save answers',
    submit: 'Submit response',
    submitted: 'Submitted {date}. Thank you — the evaluation team can see your answers.',
    submitConfirm: 'Submit your response? You can still reopen this link to update it.',
    dueAt: 'Answer by {date}',
    sourceTitle: 'Specification',
    templateCta: 'Download response template',
    uploadTitle: 'Your documents',
    documentsTitle: 'Documents',
    questionsTitle: 'Questions',
    extraUpload: 'Anything else you want to send',
    filterMust: 'Must',
    uploadLead: 'Technical offer, commercial offer, Q&A or presentation.',
    uploaded: 'Uploaded',
    readOnly: 'This evaluation is closed. Your answers are read-only.',
    readOnlySubmitted: 'Your response was submitted and is read-only. Ask the evaluation team to reopen it if you need to change something.',
    expired: 'This link has expired. Ask your contact for a new one.',
    mustAnswer: 'Answer every must-requirement before submitting.',
    privacy: 'Only the evaluation team sees your answers. Other vendors never do.',
    requiredTitle: 'Required documents',
    requiredProgress: '{done} of {total} required items done',
    requiredMandatory: 'Mandatory',
    requiredOptional: 'Optional',
    requiredUpload: 'Upload',
    requiredReplace: 'Replace',
    requiredNotUploaded: 'Not yet uploaded',
    requiredFormats: 'Accepted: {formats}',
    mustUploadDocuments: 'Answer every mandatory item before submitting.',
    choicePlaceholder: 'Choose one',
    yes: 'Yes',
    no: 'No',
    requirementsTitle: 'Requirements',
    colId: 'ID',
    colRequirement: 'Requirement',
    colStatus: 'Status',
  },

  // The account-free contributor link (6 Oct 2026) — the scoring screen's
  // own closest precedent for "no session, no account" is the vendor
  // portal above; this is the same idea for a stakeholder's own card.
  contributor: {
    invalid: 'Ask your contact for a new link.',
    pinTitle: 'Enter your access code',
    pinLabel: 'Access code',
    pinSubmit: 'Continue',
    pinWrong: 'That code was not accepted.',
    pinLimited: 'Too many attempts. Wait a few minutes and try again.',
    decisionPosition: 'Decision {n} of {total}',
    skip: 'Skip for now',
    keyBack: 'back',
  },

  admin: {
    refresh: 'Refresh',
    address: 'Address',
    addressPlaceholder: 'shared',
    addressSet: 'Address saved — point DNS at this server',
    addressCleared: 'Address removed',
    customer: 'Customer',
    noCustomer: 'Unassigned',
    assigned: 'Workspace assigned',
    unassigned: 'Workspace unassigned',
    title: 'Operator console',
    signIn: 'Operator sign-in',
    tokenLabel: 'Operator token',
    overview: 'Overview',
    workspaces: 'Workspaces',
    leads: 'Enquiries',
    expertReviews: 'Expert reviews',
    aiCredits: 'Credit requests',
    totalWorkspaces: 'Workspaces',
    aiWorkspaces: 'On AI',
    demoWorkspaces: 'Demo',
    openLeads: 'Open enquiries',
    openReviews: 'Open review requests',
    openCreditRequests: 'Open credit requests',
    signingWarning: 'Licence signing key',
    enableAi: 'Enable AI (1 year)',
    setExpiry: 'Set expiry',
    manage: 'Manage',
    suspend: 'Suspend',
    reactivate: 'Reactivate',
    lastActivity: 'Last activity',
    aiRuns: 'AI runs',
    seats: 'Seats',
    markContacted: 'Mark contacted',
    markClosed: 'Close',
    markScheduled: 'Mark scheduled',
    markGranted: 'Mark granted',
  },

  errors: {
    generic: 'Something went wrong.',
    notFound: 'This page does not exist.',
    notFoundLead: 'The link may be old, or the evaluation may have been removed.',
    forbidden: 'You do not have access to this.',
    offline: 'The server did not respond. Check your connection and try again.',
    sessionExpired: 'Your session has ended. Sign in again.',
    backHome: 'Back to your evaluations',
    backSite: 'Back to the home page',
    // `DraftError` (web/src/lib/api.ts) — the draft engine failed with no
    // server message to show.
    draftFailed: 'The draft could not be completed. Try again.',
    draftStreamUnreadable: 'The connection to the draft engine was interrupted. Try again.',
    draftIncomplete: 'The draft finished without producing a result. Try again.',
    // `DownloadError` (web/src/lib/api.ts) — a built or served document
    // failed with no server message to show.
    decisionPackFailed: 'The decision pack could not be created. Try again.',
    reportDeckFailed: 'The overview deck could not be created. Try again.',
    responseTemplateFailed: 'The response template could not be downloaded. Try again.',
    portalTemplateFailed: 'The response template could not be downloaded. Try again.',
    sourceDocumentFailed: 'The document could not be downloaded. Try again.',
    scorecardCsvFailed: 'The scorecard export could not be downloaded. Try again.',
    rtmCsvFailed: 'The requirement matrix could not be downloaded. Try again.',
  },

  embed: {
    evaluation: 'Vendor evaluation',
    coverage: '{coverage}% scored · {claimed} of {total} contributor cards',
    noScores: 'No contributor scores yet.',
    asOf: 'As of {time}',
    unavailable: 'This view is not available.',
    stale: 'Reopened for changes since this was filed — figures shown here may be out of date.',
    shareTitle: 'Share a read-only view',
    shareLead: 'A link that shows the filed ranking and nothing else — for a SharePoint page, an intranet or a slide. Revoke it any time.',
    shareCta: 'Create a view link',
    shareLabel: 'Where will it live?',
    shareLabelPlaceholder: 'Programme page on SharePoint',
    shareEmail: 'Send to (optional)',
    shareEmailPlaceholder: 'sponsor@company.com',
    shareIframe: 'Embed code',
    shareLinkTitle: 'Read-only view link',
    shareLinkHint: 'Anyone with the link sees the ranking. It is shown once — only a hash is stored.',
    shareRevoke: 'Revoke',
    shareEmpty: 'No view links yet.',
    shareViews: '{n} views',
    shareNever: 'Not opened yet',
    shareStatusActive: 'Active',
    shareStatusRevoked: 'Revoked',
    shareStatusExpired: 'Expired',
    shareNeedsLocked: 'Publish a filed evaluation: lock it or mark it decided first.',
    shareDemoBlocked: 'Read-only links are not available in a demo workspace.',
    shareLife: 'Links stay live for 90 days.',
  },

  teams: {
    tabName: 'Hanseatic Select',
    refresh: 'Refresh',
    needsSignIn: 'Sign in to your workspace. Teams and the browser share the same account, so this is the same evaluation you see in your browser.',
    signInCta: 'Sign in with email',
    signInCancelled: 'Sign-in was cancelled.',
    ssoCta: 'Continue with Microsoft',
    signInLead: 'Sign in to open this workspace in Teams.',
    signInPrivacy: 'Your password goes to your own instance, never to Microsoft.',
    configTitle: 'Which evaluation should this tab open?',
    configLead: 'Everyone who opens the tab lands here. You can change it later from the tab menu.',
    configAll: 'All evaluations',
    configAllHint: 'Open the workspace and choose there.',
    pickProject: 'Choose an evaluation',
    openFull: 'Open the evaluation',
    openBrowser: 'Open in the browser',
    shareStage: 'Share to the meeting stage',
    coverage: '{coverage}% scored · {claimed} of {total} contributor cards claimed',
    outsideTeams: 'This page belongs to the Microsoft Teams app. In a browser it is read-only — open Hanseatic Select directly instead.',
  },

  site: {
    nav: {
      product: 'Product',
      how: 'How it works',
      pricing: 'Pricing',
      security: 'Security',
      contact: 'Contact',
      signIn: 'Sign in',
      start: 'Request access',
    },
    hero: {
      eyebrow: 'Software vendor selection, built on evidence',
      title: 'Choose your vendor on evidence, not opinion.',
      lead: 'Hanseatic Select turns your URS into weighted criteria, lets every contributor score privately, and checks each vendor\u2019s claims against their own words \u2014 so the final recommendation is backed by evidence, not the loudest voice in the room.',
      ctaPrimary: 'Request access',
      ctaSecondary: 'Watch a demo',
      note: 'Three minutes of the real product.',
      shotCaption: 'Live product screen \u2014 the filed ranking, from evidence to decision.',
      badge1: 'URS in, criteria out',
      badge2: 'Every claim traced to a quote',
      badge3: 'Audit-ready decision pack',
      highlight: 'evidence',
      chip1: 'Quote verified in the offer',
      chip2: 'Private contributor cards',
      chip3: 'weighted contributor mean',
    },
    instrument: {
      title: 'Live ranking',
      subtitle: 'Weighted mean · 1–5',
      vendorA: 'Vendor A',
      vendorB: 'Vendor B',
      vendorC: 'Vendor C',
      sectionIntegrity: 'Data integrity & Annex 11',
      sectionFit: 'Functional fit',
      sectionValidation: 'Validation & support',
      sectionCommercial: 'Commercial & viability',
      evidenceQuote: 'The audit trail records user, timestamp, old value, new value and reason for every GxP data change.',
      evidenceSource: 'Verified · Technical offer, §4.2',
      callout1Label: '01',
      callout1: 'Drag any weight. The ranking recomputes on the same arithmetic the filed decision pack uses.',
      callout2Label: '02',
      callout2: 'Yellow marks a claim traced to a line in a vendor’s own document — never an opinion.',
      // Short, localised readings of the computeSensitivity() result — composed
      // here from its structured fields (winner name, section name, percent,
      // flip vendor), not a second implementation of the sweep itself. This
      // replaces displaying the shared module's own long-form English
      // `summary` strings, which ran to two lines at this width.
      verdictRobust: '{winner} wins under every weighting tried.',
      verdictFragile: '{winner} wins — unless {section} moves to {percent}%, where {vendor} takes the lead.',
      verdictSectionRobust: '{winner} leads at every weight for {section}.',
      verdictFlipDown: '{vendor} takes the lead if {section} drops to {percent}%.',
      verdictFlipUp: '{vendor} takes the lead if {section} rises to {percent}%.',
    },
    problem: {
      title: 'Vendor selection usually falls apart in the same four places',
      p1Title: 'The criteria arrive after the demos',
      p1: 'Everyone has already formed an opinion, and the scorecard is written to fit it.',
      p2Title: 'One loud contributor sets the tone',
      p2: 'Scores drift towards the person who talks most, not the evidence.',
      p3Title: '“Fully compliant” is taken at face value',
      p3: 'Roadmap items get sold as shipping features, and nobody checks the wording.',
      p4Title: 'The rationale cannot be reconstructed',
      p4: 'Six months later, during an audit, nobody can say why this vendor won.',
    },
    how: {
      title: 'Five steps, one defensible decision',
      lead: 'The process an HLSC consultant runs in a selection workshop — now software your team runs itself.',
      s1Title: 'Start from your URS',
      s1: 'Upload the URS and RFP. We turn the requirements into a weighted criteria tree for you to approve.',
      s2Title: 'Weight what matters',
      s2: 'Sections and criteria get relative weights, agreed before anyone sees a demo.',
      s3Title: 'Score independently',
      s3: 'Each contributor gets a private card and a vote weight — no one sees another’s score.',
      s4Title: 'Check the claims',
      s4: 'Vendors respond through a private link. We check every claim against the offer and flag roadmap features sold as available.',
      s5Title: 'File the decision',
      s5: 'One pack: the weighted ranking, signatures with their meaning, flagged gaps, and the full audit trail.',
    },
    showcase: {
      title: 'What it actually looks like',
      m1Title: 'Every requirement, every vendor, side by side',
      m1: 'Each answer carries the vendor\u2019s own wording and the paragraph it came from. Unanswered stays unanswered \u2014 it is never averaged away.',
      m2Title: 'An independent read of the offers',
      m2: 'The claims are checked against the documents, roadmap features are separated from shipping ones, and the scoring is checked for the patterns that skew a result.',
    },
    features: {
      title: 'What you get',
      f1Title: 'Weighted scoring that holds up',
      f1: 'Group and criterion weights, vote weights per contributor, and a ranking that updates as scores arrive.',
      f2Title: 'Evidence, not vibes',
      f2: 'Every independent score points to a verbatim quote in the offer — nothing else is kept.',
      f3Title: 'Vendor portal',
      f3: 'Vendors respond and upload offers through a private link. No accounts, no email chasing.',
      f4Title: 'Integrity checks',
      f4: 'Statistical checks flag a card that contradicts the evidence, and lower its weight in the advisory read.',
      f5Title: 'Decision pack',
      f5: 'One audit-ready document: ranking, method, weights, quotes, gaps, signatures and the audit trail.',
      f6Title: 'Teams and private hosting',
      f6: 'Use the hosted workspace, add it as a Microsoft Teams tab, or run it in your own Docker network.',
    },
    honesty: {
      title: 'What the AI does — and what it never does',
      doesTitle: 'It does',
      does1: 'Read every offer against every criterion, and quote the passage it relied on.',
      does2: 'Label each claim as support, refute, mixed or absent, including negations.',
      does3: 'Flag roadmap language dressed up as an available feature.',
      does4: 'Show where the panel and the evidence disagree.',
      notTitle: 'It never',
      not1: 'Writes the filed score. That number is the contributor mean, always.',
      not2: 'Invents a quote. Anything not verbatim in the document is discarded.',
      not3: 'Scores an unrelated document as a match — coverage drops to zero and the document is flagged.',
      not4: 'Replaces your procurement or quality process. This is decision support, not a validated system of record — teams operating under GxP should continue to treat it as outside that boundary.',
    },
    audience: {
      title: 'Built for the people in the room',
      a1Title: 'Project lead',
      a1: 'Run the selection without building another spreadsheet.',
      a2Title: 'Quality',
      a2: 'A rationale that survives an audit, with the evidence attached.',
      a3Title: 'IT and automation',
      a3: 'Integration and architecture criteria weighted properly, not as an afterthought.',
      a4Title: 'Management',
      a4: 'One page that shows what was decided, by whom, and on what basis.',
    },
    cta: {
      title: 'Run your next selection on evidence',
      lead: 'Start free in two minutes, or look at a finished evaluation first.',
      primary: 'Start free',
      secondary: 'Watch a demo',
      tertiary: 'Talk to HLSC',
    },
    pricing: {
      title: 'Pricing',
      lead: 'Try everything free for {days} days. After that, a plan sized to your organisation.',
      recommended: 'Start here',
      perMonth: 'per workspace / month, ex VAT',
      fromPrefix: 'From',
      trialName: 'Free trial',
      trialPrice: 'Free for {days} days',
      trialNote: 'No credit card. Everything included.',
      trialLead: 'The whole product, AI tools included, on a hosted workspace.',
      trialF1: 'Unlimited evaluations and vendors',
      trialF2: 'Draft criteria from your URS',
      trialF3: 'Independent evaluation with quoted evidence',
      trialF4: 'Compliance matrix and vendor portal',
      trialF5: 'Ranking, sign-off and decision pack',
      trialF6: '3 owners, 25 contributors',
      teamName: 'Team',
      teamLead: 'One focused vendor selection, start to decision pack.',
      teamF1: '3 owners, 25 contributors',
      teamF2: '25 independent evaluations a month',
      teamF3: '25 drafts from a URS a month',
      teamF4: '1,000 assistant questions a month',
      teamF5: '50 tender refreshes a month',
      teamF6: 'Extra credits: €49 per 1,000',
      businessName: 'Business',
      businessLead: 'Several selections running at once, across teams.',
      businessF1: '10 owners, 100 contributors',
      businessF2: '75 independent evaluations a month',
      businessF3: '75 drafts from a URS a month',
      businessF4: '3,000 assistant questions a month',
      businessF5: '150 tender refreshes a month',
      businessF6: 'Extra credits: €49 per 1,000',
      enterpriseName: 'Enterprise',
      enterpriseLead: 'Your own instance, sized and supported for scale.',
      enterpriseF1: 'Unlimited owners and contributors',
      enterpriseF2: 'Dedicated or on-premise instance',
      enterpriseF3: 'Microsoft Teams and Entra single sign-on',
      enterpriseF4: 'Priority support',
      enterpriseF5: 'Custom monthly allowance',
      enterpriseF6: 'HLSC expert review of your selections',
      talk: 'Talk to HLSC',
      mailSubject: 'Hanseatic Select — Enterprise',
      faqTitle: 'Questions people ask before buying',
      q1: 'What happens when the trial ends?',
      a1: 'The AI tools switch off and your evaluations, scores and decision packs stay where they are. Talk to us about a plan to carry on with everything.',
      q2: 'Where is our data?',
      a2: 'In the workspace you signed up for, or in your own instance if you run it yourself. Vendor documents are stored as text for the evaluation and nothing is shared between organisations.',
      q3: 'Is this a validated GxP system?',
      a3: 'No, and we do not claim it is. It is decision support for any software vendor selection: it produces the evidence and the rationale, and your own process — quality, procurement or otherwise — does the rest.',
      q4: 'Can we run it in our own network?',
      a4: 'Yes. Enterprise can be a dedicated instance or an installation on your own servers, including a model you host.',
    },
    security: {
      title: 'Security and compliance',
      lead: 'What the product does with your evaluation, in plain terms.',
      accessTitle: 'Access and separation',
      access1: 'Every request is checked against the organisation that owns the evaluation.',
      access2: 'Contributors only ever see their own scorecard.',
      access3: 'Vendor links are scoped to one vendor, expire, and can be revoked at any time.',
      access4: 'Passwords are stored as salted scrypt hashes; PINs the same way.',
      dataTitle: 'Your data',
      data1: 'Documents are stored as extracted text, inside your workspace.',
      data2: 'Nothing is shared between organisations, and nothing is used to train a model.',
      data3: 'Demo workspaces are deleted automatically, including everything in them.',
      data4: 'The decision pack and CSV export mean your records are never locked in.',
      aiTitle: 'AI processing',
      ai1: 'The independent evaluation sends evaluation text to a model provider when the AI tools are enabled.',
      ai2: 'On a private instance you choose the endpoint, including a model on your own hardware.',
      ai3: 'With AI switched off, the lexical and language engines still run locally.',
      trailTitle: 'Audit trail',
      trail1: 'Every change is recorded with actor, action and timestamp.',
      trail2: 'Sign-offs record the signatory, the meaning and the moment.',
      trail3: 'Locking makes scores read-only; decided is final.',
      contactTitle: 'Reporting a problem',
      contactLead: 'Found a security issue? Write to {email} and we will come back to you.',
      note: 'Hanseatic Select is a decision-support tool. It does not replace your procurement or quality process, and organisations operating under GxP should continue to treat it as outside their validated system boundary.',
    },
    contact: {
      title: 'Talk to us',
      name: 'Your name',
      message: 'What are you selecting?',
      send: 'Write to us',
      stepTopic: 'What is this about?',
      topicAccess: 'Request access',
      topicSelection: 'Talk through a selection',
      topicSecurity: 'Security and private hosting',
      topicPricing: 'Plans and pricing',
      stepDetails: 'Add a line of context',
      optional: 'optional',
      organisation: 'Organisation',
      context: 'What are you selecting?',
      organisationLine: 'Organisation: {value}',
      sheetTitle: 'Your message',
      sheetTo: 'To',
      sheetSubject: 'Subject',
      subjectAccess: 'Access request: Hanseatic Select',
      subjectSelection: 'A vendor selection: Hanseatic Select',
      subjectSecurity: 'Security and hosting: Hanseatic Select',
      subjectPricing: 'Plans and pricing: Hanseatic Select',
      introAccess: 'I would like access to Hanseatic Select for my organisation.',
      introSelection: 'I would like to talk through a vendor selection we are planning.',
      introSecurity: 'I have questions about security and private hosting for Hanseatic Select.',
      introPricing: 'I would like to know which plan fits my organisation.',
      signOff: 'Kind regards,',
      demoTitle: 'Rather see it first?',
      officeTitle: 'Hanseatic LifeScience Consulting',
    },
    legal: {
      imprint: 'Imprint',
      privacy: 'Privacy',
      terms: 'Terms',
      managingDirectors: 'Managing directors',
      vatId: 'VAT ID',
      responsible: 'Responsible for content',
      draftNotice: 'Draft for legal review before launch.',
    },
    footer: {
      product: 'Product',
      company: 'Company',
      legal: 'Legal',
      rights: 'All rights reserved.',
      built: 'Built by life-science consultants in Buchholz, Germany.',
    },
  },

  report: {
    title: 'Report',
    openCta: 'Open the report',
    contents: 'Contents',
    navOutcome: 'Outcome',
    navRanking: 'Ranking',
    navMethod: 'Weights and method',
    navEvidence: 'Evidence',
    navCompliance: 'Compliance',
    navGaps: 'Gaps and integrity',
    navStability: 'Stability',
    navSignoff: 'Sign-off and audit',
    provisional: 'Provisional',
    filed: 'Filed',
    downloadTitle: 'Take this away',
    downloadPdf: 'Detailed record',
    downloadPdfDesc: 'Every section, every citation, the audit trail and the wet-ink page — a print-ready record.',
    downloadDeck: 'Overview deck',
    downloadDeckDesc: 'Seven slides for a steering committee: the decision, the ranking, the evidence and how stable it is.',
    downloadCsv: 'Scorecard export',
    downloadCsvDesc: 'One row per section, criterion and vendor, with the combined score.',
    downloadRtm: 'Requirement matrix',
    downloadRtmDesc: 'One row per requirement, with every vendor’s compliance and quote.',
    outcome: 'Outcome',
    leading: 'Leading',
    sealTitle: 'Registry seal',
    sealNone: 'UNSEALED — no current signature covers this content.',
    sealedBy: 'Sealed by {name} · {date}',
    method: 'Weights and method',
    methodEmpty: 'No sections are defined yet.',
    engineHeuristic: 'Independent read of the offers, built-in rules only — no language model was used.',
    engineActive: 'Independent read of the offers, language model assisted.',
    rankCol: 'Rank',
    vendorCol: 'Vendor',
    weightedMean: 'Weighted mean',
    sectionCol: 'Section',
    criterionCol: 'Criterion',
    weightCol: 'Weight',
    sectionWeight: 'Section weight',
    criteriaTotal: 'Criteria total',
    sectionsTotal: 'Sections total',
    evidence: 'Evidence',
    evidenceEmpty: 'No quoted evidence on record yet.',
    evidenceShowMore: 'Show {n} more',
    evidenceShowLess: 'Show fewer',
    compliance: 'Compliance',
    complianceEmpty: 'No compliance matrix on record yet.',
    complianceOpen: 'Open the compliance matrix',
    gaps: 'Gaps and integrity',
    gapsEmpty: 'No gap between the independent read and the contributor panel stands out.',
    gapTypeCol: 'Type',
    gapDeltaCol: 'Delta',
    gapFlagged: 'Flagged',
    stability: 'Stability',
    stabilityEmpty: 'No sensitivity read is available yet.',
    stabilityWinner: 'Winner',
    stabilityMargin: 'Margin to runner-up',
    stabilityCurrentCol: 'Current weight',
    stabilitySummaryCol: 'What would change it',
    auditTitle: 'Audit trail',
    auditEmpty: 'No audit history on record yet.',
  },

  present: {
    entryCta: 'Present',
    entryHint: 'Present this evaluation live',
    frameTitle: 'Overview deck presentation',
    loading: 'Preparing the deck…',
    buildFailed: 'The overview deck could not be built right now.',
    buildFailedLead: 'Nothing was lost — try again in a moment.',
    retryCta: 'Try again',
    fullscreenEnter: 'Enter full screen',
    fullscreenExit: 'Exit full screen',
    exitCta: 'Exit presentation',
  },
};
